import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { hasMultiParlorComparePath } from "@/utils/parlorCompareVisitStorage";

/**
 * multi / non_multi 세그먼트 (페이지 무관).
 *
 * - multi: compare 방문 스토리지(TTL 내) distinct 식장 ≥ 2
 * - non_multi: 그 외
 *
 * ⚠️ 이 훅은 **읽기 전용**이다. 방문 기록을 하지 않는다.
 *    식장 상세에서 방문 upsert 는 호출부(게이트/페이지) 책임.
 * ⚠️ Hackle 할당도 하지 않는다. multi/nonMulti 슬롯 children 에서만 variation 호출.
 *    (@see ParlorMultiSplitContainer)
 */
export type ParlorMultiSegment = "loading" | "multi" | "non_multi";

/** QA: `?force_parlor_multi=1|true|multi` → multi, `0|false|non_multi` → non_multi */
export function parseForceParlorMulti(
  raw: string | string[] | undefined,
): Exclude<ParlorMultiSegment, "loading"> | null {
  if (raw == null) return null;
  const v = String(Array.isArray(raw) ? raw[0] : raw)
    .trim()
    .toLowerCase();
  if (v === "1" || v === "true" || v === "multi") return "multi";
  if (v === "0" || v === "false" || v === "non_multi" || v === "non-multi") {
    return "non_multi";
  }
  return null;
}

/** 동기 판정 (localStorage). SSR/초기 페인트 전엔 호출하지 말 것. */
export function resolveMultiParlorSegment(
  forceRaw?: string | string[],
): Exclude<ParlorMultiSegment, "loading"> {
  const forced = parseForceParlorMulti(forceRaw);
  if (forced) return forced;
  return hasMultiParlorComparePath() ? "multi" : "non_multi";
}

/**
 * 어느 페이지에서든 multi 여부만 읽는다.
 * loading 동안 실험 컨테이너를 마운트하지 말 것 (오할당 방지).
 */
export function useParlorMultiSegment(): {
  segment: ParlorMultiSegment;
  isReady: boolean;
} {
  const router = useRouter();
  const [segment, setSegment] = useState<ParlorMultiSegment>("loading");

  const forceRaw = router.isReady ? router.query.force_parlor_multi : undefined;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!router.isReady) return;
    setSegment(resolveMultiParlorSegment(forceRaw));
  }, [router.isReady, forceRaw, router.asPath]);

  return {
    segment,
    isReady: segment !== "loading",
  };
}
